import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import CustomExpansionPanel, { NestedCustomExpansionPanel } from '../custom-material/expansion-panel/ExpansionPanel'
import CustomExpansionPanelDetails from '../custom-material/expansion-panel/ExpansionPanelDetails'
import { TitleCustomExpansionPanelSummary, SubtitleCustomExpansionPanelSummary } from '../custom-material/expansion-panel/ExpansionPanelSummary'
import CustomFormControlLabel, { BoldCustomFormControlLabel } from '../custom-material/form-control-label/FormControlLabel'
import { SmallCustomSwitch, LargeCustomSwitch } from '../custom-material/switch/Switch'
import CustomButton, { CancelCustomButton } from '../custom-material/button/Button'
import Divider from '@material-ui/core/Divider'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import HelpIcon from '@material-ui/icons/Help'
import SubscriptionUpdateDialogComponent from './SubscriptionUpdateDialogComponent'
import RequestUserDeactivationDialogComponent from './RequestUserDeactivationDialogComponent'
import LearnMoreButton from '../common/LearnMoreButton'
import LearnMoreDialogComponent from './LearnMoreDialogComponent'
import {
  checkProductActive,
  checkProductPending,
  checkProductPurchased,
  determineProducts,
  determineRegions,
  sortRegions
} from '../../libs/utils'
import { isEmpty } from 'lodash'

const styles = theme => ({
  title: {
    fontSize: 22,
    fontWeight: 'normal',
    paddingRight: 0
  },
  subscriptionLabel: {
    color: theme.palette.primary.main
  },
  subscriptionAccess: {
    display: 'flex'
  },
  subscriptionAccessHelp: {
    marginLeft: 4,
    fontSize: 22,
    color: '#00ACEF'
  },
  learnMoreButton: {
    marginBottom: 4,
    padding: '4px 10px'
  },
  userDeactivationPendingSwitch: {
    backgroundColor: '#A2CAEC !important'
  },
  userDeactivationDisabledSwitch: {
    backgroundColor: '#C0C0C0 !important'
  },
  productGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  productGroupTitle: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    width: '70%'
  },
  productGroupName: {
    fontSize: 16.5
  },
  productGroupDivider: {
    width: '70%',
    backgroundColor: theme.palette.primary.main
  },
  productGroupContent: {
    width: '70%',
    marginBottom: 10
  },
  productPendingLabel: {
    fontStyle: 'italic',
    color: 'rgba(0, 0, 0, 0.38)'
  },
  productPendingSwitch: {
    backgroundColor: '#A2CAEC !important'
  },
  productDisabledSwitch: {
    backgroundColor: '#C0C0C0 !important'
  },
  subscriptionFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center'
  },
  emailLink: {
    color: theme.palette.primary.main,
    fontSize: 14,
    marginTop: 10
  },
  preferencesLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none'
  },
  fullWidth: {
    width: '100%'
  },
  tooltip: {
    opacity: '1.0 !important'
  }
})

const emailParams = {
  to: 'healthcare@ipdanalytics.com',
  subject: 'Reinstate Access',
  body: 'Hello,%0D%0A%0D%0AI would like to reinstate my access to IPD Analytics. Please contact me.'
}

class SubscriptionComponent extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      dialogOpen: false,
      deactivateUserDialogOpen: false,
      learnMoreDialogOpen: false,
      learnMoreProduct: ''
    }
    this.handleOpenDialog = () => this.setState({dialogOpen: true})
    this.handleCloseDialog = () => this.setState({dialogOpen: false})
    this.handleOpenDeactivateUserDialog = () => this.setState({deactivateUserDialogOpen: true})
    this.handleCloseDeactivateUserDialog = () => this.setState({deactivateUserDialogOpen: false})
    this.handleOpenLearnMoreDialog = productName => () => this.setState({learnMoreDialogOpen: true, learnMoreProduct: productName})
    this.handleCloseLearnMoreDialog = () => this.setState({learnMoreDialogOpen: false, learnMoreProduct: ''})
  }

  render () {
    const { classes, contact, subscription, originalSubscription, updates, setSubscriptionToDefault, toggleProductStatus, toggleRegionStatus, saveSubscription, saveDeactivate, manageEmailsView } = this.props
    const { dialogOpen, deactivateUserDialogOpen, learnMoreDialogOpen, learnMoreProduct } = this.state
    if (isEmpty(subscription)) return null
    return (
      <CustomExpansionPanel defaultExpanded={!manageEmailsView}>
        <TitleCustomExpansionPanelSummary expandIcon={<ArrowRightIcon style={{fontSize: 50}} />}>
          <Typography className={classes.title}>Your Subscription</Typography>
        </TitleCustomExpansionPanelSummary>
        <CustomExpansionPanelDetails>
          <React.Fragment>
            <Typography className={classes.subscriptionAccess} variant='body2'>
              <strong>Subscription Access:</strong>&nbsp;
              Web Portal Access, Email Alerts, Analyst Consultations (Ask an Analyst)
              <Tooltip
                classes={{popper: classes.tooltip}}
                title='Set up phone consultations, submit information requests, and get quick answers to specific questions by engaging our expert analyst team of former patent litigators, patent searchers, clinical pharmacists, brand and generic forecasters, and PhD scientists using our Ask an Analyst button from anywhere on the platform'
              >
                <HelpIcon className={classes.subscriptionAccessHelp} />
              </Tooltip>
            </Typography>
            <Typography variant='body2'><strong>Product Selections:</strong></Typography>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <BoldCustomFormControlLabel
                style={{marginTop: 10, marginRight: 10}}
                label={`Status: ${subscription.contactStatus.toLowerCase() === 'active' ? 'Active User' : 'Deactivation Pending'}`}
                control={
                  <LargeCustomSwitch
                    classes={{track: subscription.contactStatus === 'Pending Removal' ? classes.userDeactivationDisabledSwitch : subscription.contactStatus.toLowerCase() !== 'active' ? classes.userDeactivationPendingSwitch : ''}}
                    disableRipple
                    checked={subscription.contactStatus.toLowerCase() === 'active'}
                    onChange={this.handleOpenDeactivateUserDialog}
                    disabled={subscription.contactStatus === 'Pending Removal'}
                  />
                }
              />
              {subscription.contactStatus === 'Pending Removal'
                ? <a
                  className={classes.emailLink}
                  href={`mailto:${emailParams.to}?subject=${emailParams.subject}&body=${emailParams.body}`}
                >
                  Contact us to reinstate your access
                </a>
                : null
              }
            </div>
            {isEmpty(subscription.productGroups)
              ? <React.Fragment /> // @TODO - empty state
              : subscription.productGroups.map((group, productGroupIndex) =>
                <React.Fragment key={productGroupIndex}>
                  <div className={classes.productGroupTitle}>
                    <Typography className={classes.productGroupName} variant='body2'>{(group.name || '').toUpperCase()}</Typography>
                    <LearnMoreButton classes={classes} group={group} handleOpen={this.handleOpenLearnMoreDialog} subscription={subscription} />
                  </div>
                  <Divider className={classes.productGroupDivider} />
                  <div className={classes.productGroupContent}>
                    {isEmpty(group.products)
                      ? <React.Fragment /> // @TODO - empty state
                      : determineProducts(group.products).map((product, productIndex) =>
                        <div className={classes.productGroupProduct} key={productIndex}>
                          <CustomFormControlLabel
                            classes={{root: classes.fullWidth, label: classes.fullWidth}}
                            label={<div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                              <span>{product.name} {checkProductPending(product.regions) ? <span className={classes.productPendingLabel}>{product.name === 'Pharma' || product.name === 'Biologics' ? '(Pending)' : product.regions[0].operationType === 'Add' ? '(Pending Activation)' : '(Pending Deactivation)'}</span> : ''}</span>
                              <LearnMoreButton classes={classes} group={group} product={product} handleOpen={this.handleOpenLearnMoreDialog} subscription={subscription} />
                            </div>}
                            control={
                              <SmallCustomSwitch
                                classes={{track: !checkProductPurchased(product.regions) || checkProductPending(originalSubscription.productGroups[productGroupIndex].products[productIndex].regions) || subscription.contactStatus === 'Pending Removal' ? classes.productDisabledSwitch : checkProductPending(product.regions) ? classes.productPendingSwitch : ''}}
                                disableRipple
                                checked={checkProductActive(product.regions)}
                                disabled={!checkProductPurchased(product.regions) || checkProductPending(originalSubscription.productGroups[productGroupIndex].products[productIndex].regions) || subscription.contactStatus === 'Pending Removal'}
                                onChange={toggleProductStatus(group.name, product.name)}
                              />
                            }
                          />
                          {isEmpty(product.regions) || product.regions.length === 1
                            ? <React.Fragment />
                            : <NestedCustomExpansionPanel defaultExpanded>
                              <SubtitleCustomExpansionPanelSummary expandIcon={<ArrowRightIcon style={{fontSize: 32}} />}>
                                <Typography>REGION(S)</Typography>
                              </SubtitleCustomExpansionPanelSummary>
                              <CustomExpansionPanelDetails style={{paddingTop: 0}}>
                                {sortRegions(determineRegions(product.regions)).map((region, regionIndex) =>
                                  <CustomFormControlLabel
                                    key={regionIndex}
                                    label={<span>
                                      {region.name} {(region.accessStatus || '').toLowerCase() === 'access pending' ? (region.operationType || '').toLowerCase() === 'add' ? <span className={classes.productPendingLabel}>(Pending Activation)</span> : <span className={classes.productPendingLabel}>(Pending Deactivation)</span> : ''}
                                    </span>}
                                    control={
                                      <SmallCustomSwitch
                                        classes={{track: !region.purchased || originalSubscription.productGroups[productGroupIndex].products[productIndex].regions[regionIndex].accessStatus.toLowerCase() === 'access pending' || subscription.contactStatus === 'Pending Removal' ? classes.productDisabledSwitch : (region.accessStatus || '').toLowerCase() === 'access pending' ? classes.productPendingSwitch : ''}}
                                        disableRipple
                                        checked={(region.accessStatus || '').toLowerCase() !== 'no access' && (region.operationType || '').toLowerCase() !== 'remove'}
                                        disabled={!region.purchased || originalSubscription.productGroups[productGroupIndex].products[productIndex].regions[regionIndex].accessStatus.toLowerCase() === 'access pending' || subscription.contactStatus === 'Pending Removal'}
                                        onChange={toggleRegionStatus(group.name, product.name, region.name)}
                                      />
                                    }
                                  />
                                )}
                              </CustomExpansionPanelDetails>
                            </NestedCustomExpansionPanel>
                          }
                        </div>
                      )
                    }
                  </div>
                </React.Fragment>
              )
            }
            <br />
            <div className={classes.subscriptionFooter}>
              {/* <Typography variant='body2'>To make changes to your email preferences and favorites, <Link className={classes.preferencesLink} to='/'>click here</Link>.</Typography> */}
              <div />
              <div className={classes.actionButtons}>
                <CancelCustomButton
                  style={{marginRight: 8}}
                  variant='contained'
                  disabled={isEmpty(updates.products) || subscription.contactStatus === 'Pending Removal'}
                  size='small'
                  color='default'
                  
                  onClick={setSubscriptionToDefault}
                >
                  Cancel
                </CancelCustomButton>
                <CustomButton
                  variant='contained'
                  size='small'
                  color='secondary'
                  disabled={isEmpty(updates.products) || subscription.contactStatus === 'Pending Removal'}
                  
                  onClick={this.handleOpenDialog}
                >
                  Save Changes
                </CustomButton>
              </div>
            </div>
            {deactivateUserDialogOpen ? <RequestUserDeactivationDialogComponent dialogOpen={deactivateUserDialogOpen} handleCloseDialog={this.handleCloseDeactivateUserDialog} saveDeactivate={saveDeactivate} /> : null}
            {dialogOpen ? <SubscriptionUpdateDialogComponent dialogOpen={dialogOpen} updates={updates} handleCloseDialog={this.handleCloseDialog} saveSubscription={saveSubscription} /> : null}
            {learnMoreDialogOpen
              ? <LearnMoreDialogComponent
                dialogOpen={learnMoreDialogOpen}
                handleCloseDialog={this.handleCloseLearnMoreDialog}
                majorCategory={subscription.majorCategory}
                minorCategory={subscription.minorCategory}
                subClass={subscription.subClass}
                productName={learnMoreProduct}
                contact={contact}
              />
              : null
            }
          </React.Fragment>
        </CustomExpansionPanelDetails>
      </CustomExpansionPanel>
    )
  }
}

SubscriptionComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  contact: PropTypes.object,
  subscription: PropTypes.object,
  originalSubscription: PropTypes.object,
  setSubscriptionToDefault: PropTypes.func.isRequired,
  toggleContactStatus: PropTypes.func.isRequired,
  toggleProductStatus: PropTypes.func.isRequired,
  toggleRegionStatus: PropTypes.func.isRequired,
  saveSubscription: PropTypes.func.isRequired,
  saveDeactivate: PropTypes.func.isRequired
}

export default withStyles(styles)(SubscriptionComponent)
